/**
 * SelectPlan — Plan-Auswahl mit Promo-Code, ersetzt die alte /checkout-Seite.
 *
 * Flow:
 *  1. Holt /api/billing/plans (3 Pläne × 2 Cycles + Onboarding-Fee).
 *  2. User wählt Plan + Cycle (Tabs Monatlich / 6 Monate).
 *  3. Optionaler Promo-Code → POST /billing/redeem-promo prüft live.
 *  4. „Jetzt zahlen & starten" → /billing/create-session → Redirect zu Stripe.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, Check, Sparkles, Gift, Shield, FileText, ArrowRight, Tag, X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NovatusLogo } from "@/components/NovatusLogo";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const PLAN_HIGHLIGHTS = {
  starter: ["Bis 25 Projekte/Monat", "1 Nutzer", "PDF-Angebote", "E-Mail-Support"],
  pro: ["Unbegrenzte Projekte", "Bis 5 Nutzer im Team", "Erweiterte Analytics", "Priority Support", "PV-Modul"],
  enterprise: ["Alles aus Pro", "Unbegrenzte Nutzer", "Multi-Mandant", "Dedicated Account Manager", "SLA 99,9%"],
};

const fmt = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0);

export default function SelectPlan() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(5);
  const [onboardingFee, setOnboardingFee] = useState(1200);
  const [cycleId, setCycleId] = useState("six_months");
  const [planId, setPlanId] = useState("pro");
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/billing/plans").then((r) => {
      setPlans(r.data.plans || []);
      setTrialDays(r.data.trial_days || 5);
      setOnboardingFee(r.data.onboarding_fee_eur || 1200);
    }).catch(() => toast.error("Pläne konnten nicht geladen werden"));
  }, []);

  const selectedPlan = plans.find((p) => p.plan_id === planId);
  const selectedCycle = selectedPlan?.cycles.find((c) => c.cycle_id === cycleId);

  const summary = useMemo(() => {
    if (!selectedCycle) return null;
    const discount = Math.min(onboardingFee, promo?.onboarding_discount_eur || 0);
    const onboardingNet = Math.max(0, onboardingFee - discount);
    const total = onboardingNet + selectedCycle.plan_amount_eur;
    return {
      planAmount: selectedCycle.plan_amount_eur,
      onboardingDiscount: discount,
      onboardingNet,
      total,
    };
  }, [selectedCycle, promo, onboardingFee]);

  const handleApplyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setPromoLoading(true);
    try {
      const r = await api.post("/billing/redeem-promo", { code });
      setPromo(r.data);
      toast.success(`Promo-Code „${r.data.code}" aktiv`, {
        description: `Rabatt: ${fmt(r.data.onboarding_discount_eur)} auf die Onboarding-Gebühr.`,
      });
    } catch (e) {
      setPromo(null);
      toast.error(e?.response?.data?.detail || "Promo-Code ungültig");
    } finally {
      setPromoLoading(false);
    }
  };

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const r = await api.post("/billing/create-session", {
        origin_url: window.location.origin,
        plan_id: planId,
        billing_cycle: cycleId,
        promo_code: promo?.code || undefined,
      });
      if (r.data?.already_active) {
        toast.success("Abo bereits aktiv");
        navigate("/dashboard", { replace: true });
        return;
      }
      if (r.data?.url) {
        window.location.href = r.data.url;
        return;
      }
      toast.error("Stripe-Checkout konnte nicht gestartet werden");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  if (plans.length === 0) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <Loader2 className="text-[#00E575] animate-spin" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100 px-4 py-8 sm:py-12">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <NovatusLogo size="md" />
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-zinc-300">Angemeldet als</div>
            <div className="text-sm font-mono text-zinc-300">{user?.email || user?.phone}</div>
          </div>
        </div>

        <div className="text-center mb-8 sm:mb-10">
          <h1 className="font-display text-3xl sm:text-4xl font-semibold mb-2">Plan auswählen</h1>
          <p className="text-zinc-400 max-w-xl mx-auto">
            {trialDays} Tage kostenlos testen — PDF-Export erst nach Trial-Ende verfügbar. Jederzeit kündbar nach Ablauf der Mindestlaufzeit.
          </p>
        </div>

        {/* Billing-Cycle Toggle */}
        <div className="flex justify-center mb-6" role="tablist">
          <div className="inline-flex p-1 rounded-xl border border-zinc-800 bg-[#18181B]">
            {["monthly", "six_months"].map((c) => (
              <button
                key={c}
                data-testid={`plan-cycle-${c}`}
                onClick={() => setCycleId(c)}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${cycleId === c ? "bg-[#00E575] text-[#09090B]" : "text-zinc-400 hover:text-zinc-100"}`}
              >
                {c === "monthly" ? "Monatlich" : "6 Monate (1 Monat gratis)"}
              </button>
            ))}
          </div>
        </div>

        {/* Plan-Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {plans.map((p) => {
            const cycle = p.cycles.find((c) => c.cycle_id === cycleId);
            const isSelected = planId === p.plan_id;
            const isRecommended = p.plan_id === "pro";
            return (
              <button
                key={p.plan_id}
                type="button"
                data-testid={`plan-card-${p.plan_id}`}
                onClick={() => setPlanId(p.plan_id)}
                className={`relative text-left p-5 rounded-2xl border-2 transition-all ${isSelected ? "border-[#00E575] bg-[#00E575]/[0.05] shadow-lg shadow-[#00E575]/10" : "border-zinc-800 bg-[#18181B] hover:border-zinc-700"}`}
              >
                {isRecommended && (
                  <div className="absolute -top-3 left-4 px-2 py-0.5 text-[12px] uppercase tracking-widest rounded-md bg-[#00E575] text-[#09090B] font-bold">
                    Empfohlen
                  </div>
                )}
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display text-xl font-semibold">{p.label}</h3>
                  {isSelected && (
                    <div className="w-6 h-6 rounded-full bg-[#00E575] flex items-center justify-center">
                      <Check size={14} className="text-[#09090B]" />
                    </div>
                  )}
                </div>
                <div className="mb-3">
                  <span className="text-3xl font-bold">{fmt(p.monthly_eur)}</span>
                  <span className="text-sm text-zinc-400 font-medium"> / Monat</span>
                </div>
                {cycleId === "six_months" && (
                  <div className="text-[12px] text-[#00E575] mb-3">
                    {fmt(cycle.plan_amount_eur)} für 6 Mon. — spart {fmt(cycle.saves_eur)}
                  </div>
                )}
                <ul className="space-y-1.5 text-sm text-zinc-300 font-medium">
                  {(PLAN_HIGHLIGHTS[p.plan_id] || []).map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={14} className="text-[#00E575] mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        {/* Promo-Code */}
        <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Gift size={16} className="text-[#00E575]" />
            <h3 className="text-sm font-semibold uppercase tracking-widest text-zinc-300">Promo-Code</h3>
            {promo && (
              <button
                onClick={() => { setPromo(null); setPromoInput(""); }}
                className="ml-auto p-1 text-zinc-500 hover:text-zinc-200"
                data-testid="plan-promo-clear"
              >
                <X size={14} />
              </button>
            )}
          </div>
          {promo ? (
            <div className="flex items-center gap-3 p-3 bg-[#0d2a1a] border border-[#00E575]/30 rounded-lg">
              <Tag size={16} className="text-[#00E575] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-mono font-bold text-[#00E575]">{promo.code}</div>
                <div className="text-[12px] text-zinc-400">
                  {promo.label} — Onboarding-Rabatt: {fmt(promo.onboarding_discount_eur)}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Input
                data-testid="plan-promo-input"
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                placeholder="z.B. SOMMER25"
                className="h-11 bg-[#09090B] border-zinc-700 font-mono uppercase tracking-wider"
                onKeyDown={(e) => { if (e.key === "Enter") handleApplyPromo(); }}
              />
              <Button
                data-testid="plan-promo-apply"
                onClick={handleApplyPromo}
                disabled={!promoInput.trim() || promoLoading}
                variant="outline"
                className="h-11 border-zinc-700 text-zinc-100"
              >
                {promoLoading ? <Loader2 size={14} className="animate-spin" /> : "Einlösen"}
              </Button>
            </div>
          )}
        </div>

        {/* Order Summary */}
        {summary && selectedCycle && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mb-5" data-testid="plan-summary">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-zinc-300 mb-3">Ihre Bestellung</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-zinc-300">
                <span>{selectedPlan.label} — {cycleId === "monthly" ? "1 Monat" : "6 Monate (5 berechnet)"}</span>
                <span className="font-mono">{fmt(summary.planAmount)}</span>
              </div>
              <div className="flex justify-between text-zinc-300">
                <span>Onboarding-Gebühr</span>
                <span className="font-mono">{fmt(onboardingFee)}</span>
              </div>
              {summary.onboardingDiscount > 0 && (
                <div className="flex justify-between text-[#00E575]">
                  <span>Promo-Rabatt ({promo?.code})</span>
                  <span className="font-mono">− {fmt(summary.onboardingDiscount)}</span>
                </div>
              )}
              <div className="border-t border-zinc-800 pt-2 mt-2 flex justify-between items-baseline">
                <span className="text-zinc-100 font-semibold">Heute zu zahlen</span>
                <span className="font-mono text-xl font-bold text-[#00E575]" data-testid="plan-summary-total">
                  {fmt(summary.total)}
                </span>
              </div>
              <div className="text-[12px] text-zinc-300 text-right">
                inkl. {trialDays} Tage Trial · PDF-Export erst nach Trial verfügbar
              </div>
            </div>
          </div>
        )}

        {/* Trust badges */}
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-zinc-300 mb-5">
          <div className="flex items-center gap-1.5"><Shield size={12} className="text-[#00E575]" /> SSL-verschlüsselt via Stripe</div>
          <div className="flex items-center gap-1.5"><Sparkles size={12} className="text-[#00E575]" /> {trialDays} Tage Trial</div>
          <div className="flex items-center gap-1.5"><FileText size={12} className="text-[#00E575]" /> Rechnung als PDF</div>
        </div>

        {/* CTA */}
        <Button
          data-testid="plan-checkout-btn"
          onClick={handleCheckout}
          disabled={loading || !summary}
          className="h-14 w-full bg-[#00E575] text-[#09090B] font-semibold text-lg rounded-xl hover:bg-[#00C964] active:scale-[0.98] transition-all disabled:opacity-50"
        >
          {loading ? <Loader2 className="animate-spin" /> : <>Jetzt zahlen & starten <ArrowRight size={20} className="ml-2" /></>}
        </Button>
        <p className="text-center text-[12px] text-zinc-300 mt-3">
          Sie werden zu Stripe weitergeleitet. Dort können Sie alternativ einen anderen Promo-Code eingeben.
        </p>
      </div>
    </div>
  );
}
